/* Guest cart copy for Klaviyo. Only for visitors who are NOT logged in and whom Klaviyo already
   recognises in this browser (cookie from an earlier mail click or form). Writes the complete cart
   as profile property gb_cart in the same shape as the gb-cart-test service, so the Added to Cart
   mail can show every product. Logged-in customers never load this file; their gb_cart belongs to
   the service. On a device that was used with an account (logged out now) it still writes, so the
   mail matches the Added to Cart event, but never an empty cart: logging out may empty the device
   cart, and a pending mail must keep the account cart in that case. */
(() => {
  'use strict';
  if (window.gbCartGuest || document.getElementById('gb-cart-sync-config')) return;
  const VERSION = 'gast-2';
  const DOMAIN = 'greenbomb.de';
  const STORAGE_KEY = 'gb-cart-guest:last';
  const IDLE_MS = 1500;
  // Rewrite an unchanged cart after this time: the service may have written the account cart meanwhile.
  const REFRESH_MS = 600000;
  const root = window.Shopify?.routes?.root || '/';
  const nativeFetch = window.fetch.bind(window);
  const storage = {
    get: key => { try { return localStorage.getItem(key); } catch { return null; } },
    set: (key, value) => { try { localStorage.setItem(key, value); } catch { /* private mode */ } }
  };
  // Set by gb-cart-sync.js: this browser was used with a customer account before.
  const accountBrowser = () => Boolean(storage.get('gb-cart:last-customer'));
  const money = (cents, currency) => cents === null || cents === undefined ? null
    : `${(cents / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency === 'EUR' ? '€' : currency}`;
  const euro = cents => money(cents, 'EUR');

  const products = new Map();
  async function product(handle) {
    if (!products.has(handle)) {
      products.set(handle, nativeFetch(`${root}products/${encodeURIComponent(handle)}.js`, { credentials: 'same-origin' })
        .then(response => response.ok ? response.json() : null).catch(() => null));
    }
    return products.get(handle);
  }

  async function snapshot(cart) {
    const currency = cart.currency;
    const items = await Promise.all(cart.items.map(async item => {
      const data = await product(item.handle);
      const variant = data?.variants?.find(v => v.id === item.variant_id);
      const unit = variant ? variant.price : item.original_price;
      const compare = variant && variant.compare_at_price > unit ? variant.compare_at_price : null;
      return {
        variant_id: String(item.variant_id), quantity: item.quantity,
        properties: item.properties || {}, selling_plan: item.selling_plan_allocation ? String(item.selling_plan_allocation.selling_plan.id) : null,
        line_price_before_promotions: item.original_line_price,
        line_price_before_promotions_display: money(item.original_line_price, currency),
        line_price_final: item.final_line_price,
        line_price_final_display: money(item.final_line_price, currency),
        line_discount: item.total_discount,
        line_discount_display: money(item.total_discount, currency),
        discount_title: item.total_discount > 0 ? (item.discounts?.[0]?.title || '') : '',
        title: item.product_title, variant: item.variant_title || '', sku: item.sku || '',
        url: `https://${DOMAIN}/products/${item.handle}?variant=${item.variant_id}`,
        image: item.image || null,
        catalog_unit_price: (unit / 100).toFixed(2),
        catalog_unit_price_display: euro(unit),
        line_catalog_total: unit * item.quantity,
        line_catalog_total_display: euro(unit * item.quantity),
        catalog_compare_at_price_display: compare ? euro(compare) : null,
        catalog_compare_at_price: compare ? (compare / 100).toFixed(2) : null
      };
    }));
    // Free pieces (e.g. 4für3) as separate rows like the checkout. Same rule as the service (mirror.ts):
    // only when the discount covers whole pieces exactly (±1 cent).
    const rows = items.flatMap(item => {
      const before = item.line_price_before_promotions, discount = item.line_discount, quantity = item.quantity;
      if (discount <= 0 || quantity < 2) return [item];
      const piece = Math.round(before / quantity);
      if (piece <= 0 || Math.abs(piece * quantity - before) > 1) return [item];
      const free = Math.round(discount / piece);
      if (free < 1 || free >= quantity || Math.abs(free * piece - discount) > 1) return [item];
      const paid = quantity - free, catalog = Math.round(Number(item.catalog_unit_price) * 100);
      return [
        { ...item, quantity: paid,
          line_price_before_promotions: before - free * piece, line_price_before_promotions_display: money(before - free * piece, currency),
          line_discount: 0, line_discount_display: money(0, currency), discount_title: '',
          line_catalog_total: catalog * paid, line_catalog_total_display: euro(catalog * paid) },
        ...Array.from({ length: free }, (_, i) => {
          const rowDiscount = i === free - 1 ? discount - (free - 1) * piece : piece;
          return { ...item, quantity: 1, gratis: true,
            line_price_before_promotions: rowDiscount, line_price_before_promotions_display: money(rowDiscount, currency),
            line_price_final: 0, line_price_final_display: money(0, currency),
            line_discount: rowDiscount, line_discount_display: money(rowDiscount, currency),
            line_catalog_total: catalog, line_catalog_total_display: euro(catalog) };
        })
      ];
    });
    // One link that refills the whole cart on any device (Shopify cart permalink, quantities per variant).
    const perVariant = new Map();
    for (const item of cart.items) perVariant.set(item.variant_id, (perVariant.get(item.variant_id) || 0) + item.quantity);
    const cartUrl = perVariant.size
      ? `https://${DOMAIN}/cart/${[...perVariant].map(([id, qty]) => `${id}:${qty}`).join(',')}?storefront=true`
      : `https://${DOMAIN}/cart`;
    const now = Date.now();
    return {
      source: 'browser-guest', script_version: VERSION, shop: null, customer: null,
      version: now, episode: null, started_at: null, due_at: null, updated_at: now,
      checked_at: now, state: items.length ? 'active' : 'empty', eligible: items.length > 0,
      currency, items: rows, item_count: items.reduce((n, i) => n + i.quantity, 0),
      variant_ids: `,${items.map(i => i.variant_id).join(',')},`,
      product_handles: `,${[...new Set(cart.items.map(i => i.handle))].join(',')},`,
      pricing_verified_for_cart: true,
      subtotal_after_promotions: cart.total_price,
      subtotal_after_promotions_display: money(cart.total_price, currency),
      promotions_discount_total: cart.total_discount,
      promotions_discount_total_display: money(cart.total_discount, currency),
      subtotal_before_promotions_display: euro(items.reduce((sum, i) => sum + i.line_catalog_total, 0)),
      cart_url: cartUrl, test_only: false
    };
  }

  // Only the cart content decides whether a new copy is needed, not the time stamps.
  const signature = cart => JSON.stringify([cart.currency, cart.total_price,
    cart.items.map(i => [i.variant_id, i.quantity, i.final_line_price, i.original_line_price])]);

  async function identified() {
    if (!window.klaviyo?.isIdentified) return false;
    // Shopify consent API: if marketing was declined, never write (Klaviyo itself is blocked then too).
    if (window.Shopify?.customerPrivacy?.marketingAllowed && !window.Shopify.customerPrivacy.marketingAllowed()) return false;
    try { return Boolean(await window.klaviyo.isIdentified()); } catch { return false; }
  }

  let running = Promise.resolve();
  let timer = 0;
  function write() {
    running = running.then(async () => {
      if (!(await identified())) return;
      const cart = await nativeFetch(`${root}cart.js`, { credentials: 'same-origin', cache: 'no-store' }).then(r => r.json());
      if (!Array.isArray(cart.items)) return;
      if (accountBrowser() && !cart.items.length) return;
      const sig = signature(cart);
      let last = null;
      try { last = JSON.parse(storage.get(STORAGE_KEY) || 'null'); } catch { /* old or broken value */ }
      if (last?.sig === sig && Date.now() - last.at < REFRESH_MS) return;
      const copy = await snapshot(cart);
      await window.klaviyo.identify({ gb_cart: copy, gb_cart_state: copy.state, gb_cart_version: copy.version });
      storage.set(STORAGE_KEY, JSON.stringify({ sig, at: Date.now() }));
      document.documentElement.dataset.gbCartGuest = 'written';
    }).catch(() => { document.documentElement.dataset.gbCartGuest = 'error'; });
    return running;
  }
  function schedule() { clearTimeout(timer); timer = setTimeout(write, IDLE_MS); }

  const isMutation = (input, options) => {
    try {
      const url = new URL(input instanceof Request ? input.url : String(input), location.href);
      const method = options?.method || (input instanceof Request ? input.method : 'GET');
      return url.origin === location.origin && /\/cart\/(add|change|update|clear)(\.js)?\/?$/.test(url.pathname) && method.toUpperCase() === 'POST';
    } catch { return false; }
  };
  window.fetch = function(input, options) {
    const result = nativeFetch(input, options);
    if (isMutation(input, options)) result.then(schedule, schedule);
    return result;
  };
  document.documentElement.addEventListener('cart:updated', schedule);
  document.addEventListener('visibilitychange', () => { if (document.hidden && timer) { clearTimeout(timer); timer = 0; void write(); } });
  // Every page view compares once: covers cart page reloads and changes made in another tab.
  const start = () => setTimeout(write, 2500);
  if (document.readyState === 'complete') start(); else window.addEventListener('load', start, { once: true });
  window.gbCartGuest = Object.freeze({ version: VERSION, write });
})();
